import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { store } from './store';
import { SAFE, healthy, setSafeMode, stage } from './safe';
import { netStats } from './net';
import './system.css';

// Keeping the site up. One guard per page, around the one renderer and the one frame loop:
//  - Frame errors: a frame that throws is skipped and logged; a burst (5 in 3 s) pauses the loop and offers Continue,
//    Reload or Safe mode instead of a frozen tab spamming the console sixty times a second.
//  - Uncaught errors and promise rejections are logged; if the app dies before its first frame the same card appears.
//  - WebGL context loss (GPU reset, driver update, too many tabs, a phone reclaiming memory): the loop pauses and says
//    so, three.js re-creates its GPU state when the context comes back (r186 initGLContext), the image-based lighting
//    that lived only on the GPU is rebuilt, and the loop resumes. If the context does not come back in 6 s: Reload.
//  - Resolution governor: the frame time is watched (an exponential moving average); a device that cannot hold ~45 fps
//    draws fewer pixels (steps of the pixel ratio down to half), one that runs well for a while gets them back. Steps
//    wait 2.5 s after any change and ignore loading spikes, so it never oscillates. ?governor=0 turns it off.
//  - Crash log: the last 30 errors with their stage, in local storage (sd.crash), readable at __SYSTEM__.crashes().
//  - Updates: a new deploy downloads in the background and asks before it takes over (vite.config.ts).
//  - Offline: a pill says so; the cached studio and city keep working.

type Crash = { t: number; stage: string; msg: string };
const MAX_LOG = 30;

function log(msg: string) {
  const list = store.json<Crash[]>('crash', []);
  list.push({ t: Date.now(), stage: current, msg: msg.slice(0, 400) });
  store.setJson('crash', list.slice(-MAX_LOG));
}
let current = 'boot';

// ------------------------------------------------------------------ the card (one at a time) and the pills

const layer = document.createElement('div');
layer.className = 'sys-layer';
layer.setAttribute('aria-live', 'polite');
document.body.append(layer);

type Action = { label: string; primary?: boolean; run: () => void };
function card(kind: string, title: string, text: string, actions: Action[], busy = false) {
  layer.querySelector('.sys-card')?.remove();
  const el = document.createElement('section');
  el.className = `sys-card ${kind}`;
  el.setAttribute('role', 'alertdialog');
  el.innerHTML = `${busy ? '<i class="sys-spin" aria-hidden="true"></i>' : ''}<strong></strong><p></p><nav></nav>`;
  (el.querySelector('strong') as HTMLElement).textContent = title;
  (el.querySelector('p') as HTMLElement).textContent = text;
  const nav = el.querySelector('nav') as HTMLElement;
  for (const a of actions) {
    const b = document.createElement('button');
    b.textContent = a.label;
    if (a.primary) b.className = 'primary';
    b.addEventListener('click', () => { el.remove(); a.run(); });
    nav.append(b);
  }
  layer.append(el);
  (nav.querySelector('button.primary') as HTMLButtonElement | null)?.focus({ preventScroll: true });
  return el;
}
function pill(kind: string, text: string, ms = 0, action?: Action) {
  layer.querySelector(`.sys-pill.${kind}`)?.remove();
  const el = document.createElement('div');
  el.className = `sys-pill ${kind}`;
  el.textContent = text;
  if (action) {
    const b = document.createElement('button');
    b.textContent = action.label;
    b.addEventListener('click', () => { el.remove(); action.run(); });
    el.append(b);
    const x = document.createElement('button');
    x.className = 'x'; x.textContent = '×'; x.setAttribute('aria-label', 'Dismiss');
    x.addEventListener('click', () => el.remove());
    el.append(x);
  }
  layer.append(el);
  if (ms) setTimeout(() => el.remove(), ms);
  return el;
}
const reload = () => location.reload();
const safeReload = () => { setSafeMode(true); location.reload(); };

// ------------------------------------------------------------------ resolution governor

class Governor {
  private base: number;
  private scale = 1;
  private ema = 1000 / 60;
  private slowFor = 0;
  private fastFor = 0;
  private holdUntil = 0;
  private readonly native: (r: number) => void;
  readonly enabled = new URLSearchParams(location.search).get('governor') !== '0';
  static readonly STEPS = [1, 0.9, 0.8, 0.7, 0.6, 0.5];

  constructor(private renderer: THREE.WebGLRenderer) {
    this.base = renderer.getPixelRatio();
    this.native = renderer.setPixelRatio.bind(renderer);
    // the app keeps asking for the ratio it wants (city, studio); the governor scales whatever was asked for
    renderer.setPixelRatio = (r: number) => { this.base = r; this.apply(); this.hold(2500); };
    addEventListener('resize', () => this.hold(1500));
  }
  /** Ignore the next `ms` of frames (loading, shader compiles, a scene change). */
  hold(ms: number) { this.holdUntil = Math.max(this.holdUntil, performance.now() + ms); this.slowFor = this.fastFor = 0; }
  get info() { return { base: this.base, scale: this.scale, ratio: +(this.base * this.scale).toFixed(3), frameMs: +this.ema.toFixed(1) }; }

  sample(now: number, dt: number) {
    if (!this.enabled || dt > 0.25) { this.slowFor = this.fastFor = 0; return; }
    this.ema += (dt * 1000 - this.ema) * 0.05;
    if (now < this.holdUntil) return;
    if (this.ema > 22) { this.slowFor += dt; this.fastFor = 0; } else if (this.ema < 17.8) { this.fastFor += dt; this.slowFor = 0; } else { this.slowFor = this.fastFor = 0; }
    const i = Governor.STEPS.indexOf(this.scale);
    if (this.slowFor > 2 && i < Governor.STEPS.length - 1 && this.base * Governor.STEPS[i + 1] >= 0.7) this.step(Governor.STEPS[i + 1]);
    else if (this.fastFor > 6 && i > 0) this.step(Governor.STEPS[i - 1]);
  }
  private step(s: number) {
    this.scale = s;
    this.apply();
    this.hold(2500);
    dispatchEvent(new Event('resize'));   // post-processing and the computer's screen follow the new size
  }
  private apply() { this.native(this.base * this.scale); }
}

// ------------------------------------------------------------------ the guard

export class Guard {
  paused = false;
  frames = 0;
  readonly governor: Governor;
  private errors: number[] = [];
  private lostAt = 0;
  private lostTimer = 0;
  private restoreHooks: Array<() => void> = [];
  private scenes: THREE.Scene[] = [];

  constructor(private renderer: THREE.WebGLRenderer) {
    this.governor = new Governor(renderer);
    const canvas = renderer.domElement;
    canvas.addEventListener('webglcontextlost', (e) => this.lost(e), false);
    canvas.addEventListener('webglcontextrestored', () => this.restored(), false);
    addEventListener('error', (e) => { log(`${e.message} @ ${e.filename}:${e.lineno}`); this.deadOnArrival(); });
    addEventListener('unhandledrejection', (e) => { log(`unhandled: ${String((e.reason as Error)?.stack ?? e.reason)}`); });
    addEventListener('offline', () => pill('offline', 'Offline · the studio and the city keep running from the saved copy'));
    addEventListener('online', () => { layer.querySelector('.sys-pill.offline')?.remove(); pill('online', 'Back online', 2500); });
    if (!navigator.onLine) pill('offline', 'Offline · running from the saved copy');
    if (SAFE.auto) pill('safe', `Safe mode: the last visit stopped unexpectedly${SAFE.lastCrash ? ` (${SAFE.lastCrash})` : ''}, so this one runs lighter`, 0, { label: 'Full quality', run: () => { setSafeMode(false); sessionStorage.removeItem('sd.run'); location.replace(location.href.replace(/[?&]safe=1/, '')); } });
    else if (SAFE.on) pill('safe', 'Safe mode is on', 6000, { label: 'Turn off', run: () => { setSafeMode(false); location.replace(location.href.replace(/[?&]safe=1/, '')); } });
    if (SAFE.lastCrash) log(`previous run ended unexpectedly in "${SAFE.lastCrash}" (${SAFE.fails} in a row)`);
    (window as unknown as { __SYSTEM__?: unknown }).__SYSTEM__ = {
      info: () => this.info(),
      crashes: () => store.json<Crash[]>('crash', []),
      loseContext: () => renderer.forceContextLoss(),
      restoreContext: () => renderer.forceContextRestore(),
    };
  }

  /** Where the app is now (logged with errors, and remembered in case the tab is killed). */
  stage(name: string) { current = name; stage(name); this.governor.hold(3000); }
  /** Scenes whose image-based lighting must be rebuilt after a context loss. */
  watchScene(scene: THREE.Scene) { this.scenes.push(scene); }
  onRestore(fn: () => void) { this.restoreHooks.push(fn); }

  /** Run one frame of the app's loop under the guard. */
  frame(now: number, dt: number, body: () => void) {
    if (this.paused) return;
    try {
      body();
      this.frames++;
      if (this.frames === 600) healthy();   // ~10 s of clean frames
      this.governor.sample(now, dt);
    } catch (e) {
      this.frameError(e);
    }
  }

  /** A load or start-up step failed for good (after the network retries). */
  fail(where: string, e: unknown, retry?: () => void) {
    log(`${where}: ${String((e as Error)?.stack ?? e)}`);
    card('crash', `The ${where} could not load`, navigator.onLine ? 'The connection dropped or the server did not answer. Trying again usually works.' : 'This device is offline. Reconnect and try again.', [
      ...(retry ? [{ label: 'Try again', primary: true, run: retry }] : [{ label: 'Reload', primary: true, run: reload }]),
      { label: 'Safe mode', run: safeReload },
    ]);
  }

  private frameError(e: unknown) {
    console.error(e);
    log(`frame: ${String((e as Error)?.stack ?? e)}`);
    const now = performance.now();
    this.errors = this.errors.filter((t) => now - t < 3000);
    this.errors.push(now);
    if (this.errors.length < 5) return;
    this.paused = true;
    this.errors = [];
    card('crash', 'Something went wrong', 'The scene hit an error and was paused so the page stays responsive.', [
      { label: 'Continue', primary: true, run: () => { this.paused = false; } },
      { label: 'Reload', run: reload },
      { label: 'Safe mode', run: safeReload },
    ]);
  }

  private deadOnArrival() {
    // an error before the first frame has run means the app never started: say so instead of a frozen loader
    setTimeout(() => { if (this.frames === 0 && !layer.querySelector('.sys-card')) card('crash', 'The site could not start', 'Something failed while starting. A reload usually fixes it; safe mode starts lighter.', [{ label: 'Reload', primary: true, run: reload }, { label: 'Safe mode', run: safeReload }]); }, 1500);
  }

  private lost(e: Event) {
    e.preventDefault();   // tells the browser we will handle a restore
    this.paused = true;
    this.lostAt = performance.now();
    log('webgl context lost');
    card('gpu', 'Restoring the graphics…', 'The graphics processor was reset. The studio comes back in a moment.', [], true);
    clearTimeout(this.lostTimer);
    this.lostTimer = window.setTimeout(() => {
      card('gpu', 'The graphics did not come back', 'The browser has not returned the graphics context. Reloading restores everything.', [{ label: 'Reload', primary: true, run: reload }, { label: 'Safe mode', run: safeReload }]);
    }, 6000);
  }

  private restored() {
    clearTimeout(this.lostTimer);
    log(`webgl context restored after ${Math.round(performance.now() - this.lostAt)} ms`);
    this.rebuildEnvironments();
    for (const fn of this.restoreHooks) { try { fn(); } catch (err) { log(`restore hook: ${String(err)}`); } }
    layer.querySelector('.sys-card.gpu')?.remove();
    this.governor.hold(3000);
    this.paused = false;
  }

  // PMREM environment maps exist only on the GPU (render targets): after a restore they are empty and every material
  // that used one turns flat. One new RoomEnvironment (the city and the traffic both use it) replaces them all.
  private rebuildEnvironments() {
    const old = new Set<THREE.Texture>();
    for (const s of this.scenes) s.traverse((o) => {
      for (const m of ([] as THREE.Material[]).concat((o as THREE.Mesh).material ?? [])) {
        const env = (m as THREE.MeshStandardMaterial).envMap;
        if (env && (env as THREE.Texture & { isRenderTargetTexture?: boolean }).isRenderTargetTexture) old.add(env);
      }
      if (s.environment && (s.environment as THREE.Texture & { isRenderTargetTexture?: boolean }).isRenderTargetTexture) old.add(s.environment);
    });
    if (!old.size) return;
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const fresh = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();
    for (const s of this.scenes) {
      if (s.environment && old.has(s.environment)) s.environment = fresh;
      s.traverse((o) => {
        for (const m of ([] as THREE.Material[]).concat((o as THREE.Mesh).material ?? [])) {
          const sm = m as THREE.MeshStandardMaterial;
          if (sm.envMap && old.has(sm.envMap)) { sm.envMap = fresh; sm.needsUpdate = true; }
        }
      });
    }
  }

  info() {
    const r = this.renderer.info;
    const mem = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
    return {
      stage: current, frames: this.frames, paused: this.paused, safe: SAFE, governor: this.governor.info, net: netStats,
      gpu: { geometries: r.memory.geometries, textures: r.memory.textures, programs: r.programs?.length ?? 0, calls: r.render.calls, triangles: r.render.triangles },
      heapMB: mem ? Math.round(mem.usedJSHeapSize / 1048576) : null, heapLimitMB: mem ? Math.round(mem.jsHeapSizeLimit / 1048576) : null,
      storage: store.persistent ? 'local' : 'session-only',
    };
  }
}

// ------------------------------------------------------------------ updates (service worker)

/** Register the service worker once the page has settled, and offer each new deploy with a pill. */
export function registerUpdates() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  if (new URLSearchParams(location.search).get('sw') === '0') {
    // kill switch: ?sw=0 removes the worker and its caches (for support, or a broken deploy)
    void navigator.serviceWorker.getRegistrations().then((rs) => Promise.all(rs.map((r) => r.unregister())));
    void caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k))));
    return;
  }
  const start = () => void import('virtual:pwa-register').then(({ registerSW }) => {
    const update = registerSW({
      immediate: true,
      onNeedRefresh: () => pill('update', 'A new version of the site is ready', 0, { label: 'Refresh', run: () => { void update(true); } }),
      onRegisteredSW: (_url, reg) => {
        // a visitor who keeps the tab open for hours still hears about a deploy: check every 30 minutes
        if (reg) setInterval(() => { if (navigator.onLine && !document.hidden) void reg.update(); }, 30 * 60 * 1000);
      },
      onRegisterError: (e: unknown) => log(`service worker: ${String(e)}`),
    });
    // the first visit loaded the studio before the worker existed: once it controls the page, fetch the models and
    // stills this page already used again (straight from the HTTP cache) so the worker keeps them for offline visits
    const warm = () => {
      const urls = new Set(performance.getEntriesByType('resource').map((e) => e.name).filter((u) => u.startsWith(location.origin) && /\.(glb|json|webp)(\?|$)/.test(u)));
      for (const u of urls) void fetch(u, { priority: 'low' } as RequestInit).then((r) => r.arrayBuffer()).catch(() => {});
    };
    if (navigator.serviceWorker.controller) warm(); else navigator.serviceWorker.addEventListener('controllerchange', warm, { once: true });
  });
  // after the first load is done, so the worker's own downloads never compete with the studio's
  if (document.readyState === 'complete') setTimeout(start, 4000); else addEventListener('load', () => setTimeout(start, 4000), { once: true });
}
